import styled from '@emotion/styled';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import TimelineIcon from '@mui/icons-material/Timeline';
import type { Theme } from '@trinity/chat-components/types/theme';
import { shouldForwardProp } from '@trinity/chat-components/utils/emotion';

type WrapProps = {
  isDisabled?: boolean;
};

type IconProps = {
  isDisabled?: boolean;
};

export const Root = styled.button`
  display: block;
`;

export const Wrap = styled('div', {
  shouldForwardProp: shouldForwardProp('isFullPageChat', 'isDisabled'),
})<WrapProps>`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.palette.text.primary};
  cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
  border-radius: 2px;
  font-size: 14px;
  font-family: ${(props) => props.theme.fonts.regular};
  padding: 6px 8px;
  transition: background-color 0.2s;
  text-align: left;
`;

const defaultIconStyles = `
  height: 16px;
  width: 16px;
  font-size: 18px;
  margin-right: 4px;
`;

export const TextStyledIcon = styled(ArrowForwardIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const AnalysisStyledIcon = styled(AutoAwesomeIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const TimelineStyledIcon = styled(TimelineIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const CancelStyledIcon = styled(CloseIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const SearchStyledIcon = styled(SearchIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const DownloadStyledIcon = styled(DownloadIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

export const LocationStyledIcon = styled(LocationOnIcon, {
  shouldForwardProp: shouldForwardProp('isDisabled'),
})<IconProps>(defaultIconStyles);

function colorVariant(
  wl: { main?: string; dark1?: string; icon?: string } | undefined,
  enabledBg: string,
  hoverBg: string,
  disabledBg: string,
) {
  return {
    enabled: {
      backgroundColor: wl?.main || enabledBg,
      hover: { backgroundColor: wl?.dark1 || hoverBg },
    },
    disabled: {
      backgroundColor: disabledBg,
      hover: { backgroundColor: disabledBg },
    },
  };
}

function iconVariant(
  wl: { icon?: string } | undefined,
  enabledFallback: string,
  disabledColor: string,
) {
  return {
    enabled: { color: wl?.icon || enabledFallback },
    disabled: { color: disabledColor },
  };
}

export const Colors = (theme: Theme.Theme) => {
  const { gray, green, purple, wl: wlBase } = theme.palette;
  const wl = wlBase?.prompt;
  const disabledGray = theme.palette.gray.main;
  const disabledBg = theme.palette.gray.light2;

  return {
    Text: colorVariant(wl, gray.light5, gray.light6, disabledBg),
    TextIcon: iconVariant(wl, purple.light8, disabledGray),
    Analysis: colorVariant(wl, green.light3, green.light2, disabledBg),
    AnalysisIcon: iconVariant(wl, green.dark2, disabledGray),
    TimelineIcon: iconVariant(wl, purple.light8, disabledGray),
    DefaultIcon: iconVariant(wl, green.dark2, disabledGray),
    Cancel: colorVariant(wl, gray.light1, gray.light1, disabledBg),
    CancelIcon: iconVariant(wl, theme.palette.black.main, disabledGray),
  };
};
